import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  useTheme
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

// Price IDs for different subscription tiers
// These are now loaded from the global window.PRICE_IDS set in stripe-config.js
const PRICE_IDS = {
  // Standard Plan
  STANDARD_MONTHLY: "price_1RGSiCAWFWukAIzsXo55cUD9",
  STANDARD_ANNUAL: "price_1RGT4QAWFWukAIzsc4LYRx7a",

  // Pro Plan
  PRO_MONTHLY: "price_1RGSqFAWFWukAIzs81BjV9O3",
  PRO_ANNUAL: "price_1RGT0mAWFWukAIzsMvB3OtTC",

  // Premium Plan
  PREMIUM_MONTHLY: "price_1RGSsVAWFWukAIzsCBAIoEyz",
  PREMIUM_ANNUAL: "price_1RGT2AAWFWukAIzsZ8aEhFqU",
};

// Try to load from window.PRICE_IDS if available
if (window.PRICE_IDS) {
  console.log('Using PRICE_IDS from window.PRICE_IDS');
  Object.assign(PRICE_IDS, window.PRICE_IDS);
} else {
  console.log('Using hardcoded PRICE_IDS');
}

// Plan features
const PLAN_FEATURES = {
  STANDARD: [
    "Up to 500 text messages per month",
    "AI customization",
    "Email support",
    "Single business line",
    "Basic analytics"
  ],
  PRO: [
    "Up to 2,000 text messages per month",
    "Advanced AI customization",
    "Priority email & phone support",
    "Detailed analytics & reporting",
    "Team collaboration tools"
  ],
  PREMIUM: [
    "Unlimited text messages",
    "Premium AI customization",
    "24/7 priority support",
    "Advanced analytics & custom reports",
    "Dedicated account manager"
  ]
};

const SubscriptionPlans = ({ businessId, onSubscriptionComplete }) => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [loading, setLoading] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState(null);
  const { currentUser } = useAuth();
  const theme = useTheme();

  // No default payment links - we must use the ones from stripe-config.js
  // to ensure we're using the correct mode (test vs live)

  // Get payment links from the global stripe-config.js
  useEffect(() => {
    if (window.PAYMENT_LINKS) {
      console.log('Using global PAYMENT_LINKS from stripe-config.js');
      console.log('Stripe mode:', window.STRIPE_MODE);
      setPaymentLinks(window.PAYMENT_LINKS);
    } else {
      console.error('Global PAYMENT_LINKS not found. Make sure stripe-config.js is loaded.');
      // Don't set payment links if not available - this will prevent the subscription process
    }
  }, []);

  const handleBillingCycleChange = () => {
    setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly');
  };

  const handleSelectPlan = async (planType) => {
    // Check for user ID in props or localStorage
    const userId = businessId || localStorage.getItem('userId');

    if (!currentUser && !userId) {
      console.error("User not logged in and no user ID found in localStorage");
      return;
    }

    setLoading(true);

    try {
      // Determine the price ID based on plan type and billing cycle
      let priceId;

      if (planType === 'standard') {
        priceId = billingCycle === 'monthly' ? PRICE_IDS.STANDARD_MONTHLY : PRICE_IDS.STANDARD_ANNUAL;
      } else if (planType === 'pro') {
        priceId = billingCycle === 'monthly' ? PRICE_IDS.PRO_MONTHLY : PRICE_IDS.PRO_ANNUAL;
      } else if (planType === 'premium') {
        priceId = billingCycle === 'monthly' ? PRICE_IDS.PREMIUM_MONTHLY : PRICE_IDS.PREMIUM_ANNUAL;
      }

      // Get the payment link based on plan type and billing cycle
      let paymentLink;

      // Use the global payment links from stripe-config.js if available
      if (paymentLinks) {
        if (planType === 'standard') {
          paymentLink = billingCycle === 'monthly' ? paymentLinks.STANDARD_MONTHLY : paymentLinks.STANDARD_ANNUAL;
        } else if (planType === 'pro') {
          paymentLink = billingCycle === 'monthly' ? paymentLinks.PRO_MONTHLY : paymentLinks.PRO_ANNUAL;
        } else if (planType === 'premium') {
          paymentLink = billingCycle === 'monthly' ? paymentLinks.PREMIUM_MONTHLY : paymentLinks.PREMIUM_ANNUAL;
        }

        console.log(`Using ${window.STRIPE_MODE} mode payment link:`, paymentLink);
      } else {
        console.error('Payment links not available. Cannot proceed with subscription.');
        setLoading(false);
        return;
      }

      // For now, we'll use the payment links directly
      // Add user ID as client_reference_id parameter
      const userId = businessId || localStorage.getItem('userId') || currentUser?.uid;

      if (userId) {
        // Store the userId and businessId in localStorage before redirecting
        localStorage.setItem('userId', userId);
        localStorage.setItem('businessId', userId);
        localStorage.setItem('pendingPayment', 'true');
        localStorage.setItem('selectedPlan', planType);
        localStorage.setItem('billingCycle', billingCycle);
        localStorage.setItem('paymentInitiatedAt', Date.now().toString());

        // Add client_reference_id to the payment link
        const separator = paymentLink.includes('?') ? '&' : '?';
        paymentLink = `${paymentLink}${separator}client_reference_id=${userId}`;

        console.log('Using userId for payment:', userId);
        console.log('Stored payment information in localStorage');
      } else {
        console.warn('No userId found for payment. User identification after payment may fail.');
      }

      // Add success URL parameter - use window.location.origin to ensure correct domain
      // Include the businessId as a query parameter in the success URL
      const successUrl = `${window.location.origin}/payment-success?business_id=${userId}`;
      paymentLink = `${paymentLink}${paymentLink.includes('?') ? '&' : '?'}success_url=${encodeURIComponent(successUrl)}`;

      // Add metadata to the URL to ensure it's passed to the webhook
      paymentLink = `${paymentLink}&metadata[businessId]=${userId}&metadata[userId]=${userId}`;

      // Add additional metadata for better tracking
      paymentLink = `${paymentLink}&metadata[planType]=${planType}&metadata[billingCycle]=${billingCycle}`;
      paymentLink = `${paymentLink}&metadata[origin]=${encodeURIComponent(window.location.origin)}`;

      // Make sure client_reference_id is set - this is the most reliable way for Stripe to pass the user ID
      if (!paymentLink.includes('client_reference_id=')) {
        paymentLink = `${paymentLink}&client_reference_id=${userId}`;
      }

      console.log('Final payment link with all parameters:', paymentLink);

      console.log('Redirecting to payment link:', paymentLink);
      window.location.href = paymentLink;

      // In the future, we can use the Firebase function to create a checkout session
      /*
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      const result = await createCheckoutSession({
        priceId,
        businessId,
        successUrl: `${window.location.origin}/dashboard?subscription=success`,
        cancelUrl: `${window.location.origin}/pricing?subscription=canceled`
      });

      // Redirect to the checkout page
      window.location.href = result.data.url;
      */
    } catch (error) {
      console.error("Error creating checkout session:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate prices with annual discount
  const getPrice = (basePrice, cycle) => {
    if (cycle === 'annual') {
      // 20% discount for annual billing
      return Math.floor(basePrice * 0.8);
    }
    return basePrice;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
          All plans include a 14-day free trial. No credit card required to start.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
          <Typography variant="body1" color={billingCycle === 'monthly' ? 'primary' : 'text.secondary'}>
            Monthly
          </Typography>
          <Switch
            checked={billingCycle === 'annual'}
            onChange={handleBillingCycleChange}
            color="primary"
            sx={{ mx: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" color={billingCycle === 'annual' ? 'primary' : 'text.secondary'}>
              Annual
            </Typography>
            <Paper
              elevation={0}
              sx={{
                ml: 1,
                px: 1,
                py: 0.5,
                bgcolor: theme.palette.success.light,
                color: theme.palette.success.contrastText,
                borderRadius: 1
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                Save 20%
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {/* Standard Plan */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardHeader
              title="STARTER"
              titleTypographyProps={{ align: 'center', variant: 'h5' }}
              sx={{ bgcolor: theme.palette.grey[100], py: 3 }}
            />
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography component="h2" variant="h3" color="text.primary">
                  ${getPrice(39, billingCycle)}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  per month
                </Typography>
                {billingCycle === 'annual' && (
                  <Typography variant="caption" color="text.secondary">
                    billed annually (${getPrice(39, billingCycle) * 12}/year)
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <List sx={{ flexGrow: 1 }}>
                {PLAN_FEATURES.STANDARD.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>

              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => handleSelectPlan('standard')}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? 'Processing...' : 'Start Free Trial'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Pro Plan */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={5}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: `2px solid ${theme.palette.primary.main}`,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardHeader
              title="BUSINESS"
              titleTypographyProps={{ align: 'center', variant: 'h5' }}
              sx={{ bgcolor: theme.palette.primary.main, color: 'white', py: 3 }}
              action={
                <Paper
                  elevation={0}
                  sx={{
                    mr: 2,
                    mt: 1,
                    px: 1,
                    py: 0.5,
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    MOST POPULAR
                  </Typography>
                </Paper>
              }
            />
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography component="h2" variant="h3" color="text.primary">
                  ${getPrice(69, billingCycle)}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  per month
                </Typography>
                {billingCycle === 'annual' && (
                  <Typography variant="caption" color="text.secondary">
                    billed annually (${getPrice(69, billingCycle) * 12}/year)
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <List sx={{ flexGrow: 1 }}>
                {PLAN_FEATURES.PRO.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleSelectPlan('pro')}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? 'Processing...' : 'Start Free Trial'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Premium Plan */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardHeader
              title="PRO BUSINESS"
              titleTypographyProps={{ align: 'center', variant: 'h5' }}
              sx={{ bgcolor: theme.palette.grey[100], py: 3 }}
            />
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography component="h2" variant="h3" color="text.primary">
                  ${getPrice(99, billingCycle)}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  per month
                </Typography>
                {billingCycle === 'annual' && (
                  <Typography variant="caption" color="text.secondary">
                    billed annually (${getPrice(99, billingCycle) * 12}/year)
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <List sx={{ flexGrow: 1 }}>
                {PLAN_FEATURES.PREMIUM.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>

              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => handleSelectPlan('premium')}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? 'Processing...' : 'Start Free Trial'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          All plans include a 14-day free trial. No credit card required to start.
          <br />
          You can cancel anytime during your trial period.
        </Typography>
      </Box>
    </Container>
  );
};

export default SubscriptionPlans;
